import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './cos-icon.js';

/**
 * <cos-button> — Stage 41b-1, refreshed in Stage 46a.
 *
 * The first real button component in the codebase. Stage 41a.1
 * shipped a `.btn` class inside `cos-kb-article.ts` as a stand-in
 * for what this component should be; we extracted it then so the
 * rest of the editor work (Stage 41b-2) could lean on it.
 *
 * Stage 46a re-skins the variants onto the design-system tokens
 * (brand 700, charcoal, semantic danger). The Stage 41 visual
 * targets are gone; the component now matches spec § 2.7. Every
 * existing consumer keeps the same API.
 *
 * Variants:
 *   - `primary` — brand teal, white text. Use for the main
 *     action on a page or form.
 *   - `secondary` (default) — neutral white surface, 1px border.
 *     Use for any non-primary action.
 *   - `ghost` — transparent, lifts on hover. Use for inline
 *     "Undo", "Cancel", or thumbs-up/down in the helpful card.
 *   - `danger-outline` — destructive but quiet: surface fill, danger ink and
 *     border. This is the ROW-ACTION form — use it in a table cell rather
 *     than `danger`, which repeats down a column as solid red blocks.
 *   - `danger` — red, white text. Use for destructive immediate
 *     actions (NOT for Archive, which is a soft-delete and
 *     belongs to secondary).
 *   - `brand-ghost` — the brand sibling of `danger-outline`'s reasoning:
 *     a quiet, teal-tinted wash with brand text and no border. This is
 *     the ROW-ACTION form for a repeated brand-positive action (e.g. a
 *     "Renew" button on every row of a loan list) — a solid `primary`
 *     repeats down a column as loud blocks, and a plain `ghost` reads as
 *     inert for an action the user should notice is available.
 *
 * Sizes:
 *   - sm — 32px tall, 13px text, 12px horizontal padding
 *   - md (default) — 40px tall, 14px text, 16px horizontal padding
 *   - lg — 48px tall, 16px text, 20px horizontal padding
 *
 * Loading state: disables the button + swaps the start icon for
 * a small inline spinner. The label text stays so the layout
 * doesn't reflow. Set both `loading` and `disabled` if you want
 * the button to look loading-disabled without the spinner.
 *
 * The internal <button> is the click target; the Lit host
 * just hosts the slotted label and forwards type/form attributes.
 * Click events bubble naturally — consumers attach @click to
 * the cos-button host (composedPath() finds the inner button).
 *
 * Icon-only mode (2026-08-05, closes the element-audit §4 gap): set
 * `icon` + `icon-only` + `aria-label` for the close-×/pencil/row-action
 * form — the button squares to its height and the aria-label is REQUIRED
 * (a bare glyph must never reach a screen reader unnamed; dev builds
 * warn if it's missing). `aria-label` also works on a labelled button
 * when the visible text isn't specific enough. Same contract as
 * cos-edit-button's label/aria-label pair.
 *
 * ARIA pass-through (wave 3, ds-unlocks): `ariaExpanded`, `ariaHasPopup`,
 * `ariaControls`, `ariaCurrent`, `ariaPressed` mirror onto the internal
 * button as the matching `aria-*` attribute ONLY when set — unset means
 * the attribute is absent, not `"false"` or empty-string, so a button
 * that never opens anything doesn't carry a stray `aria-expanded`. This
 * unlocks disclosure/dropdown-trigger and nav-current buttons built on
 * cos-button instead of a hand-rolled native button element.
 *
 * `buttonRole` sets `role` on the internal button (e.g. `menuitem` for a
 * dropdown-menu row, `option` for a listbox row built on cos-button).
 * Unset ⇒ no role attribute, i.e. the implicit native button role.
 *
 * `pill` (attribute `pill`, reflected) swaps the corner radius to
 * `--cos-radius-pill` — the chip/CTA form (segmented-control-adjacent
 * actions, filter chips styled as buttons). Composes with every variant
 * and with icon-only (icon-only + pill = a round button).
 *
 * `dashed` (attribute `dashed`, reflected) swaps the border to a dashed
 * style — the "add one" placeholder form (an add-affordance sitting at
 * the end of a row of solid pills/chips, e.g. "+ Tag" trailing a row of
 * applied tag chips, that must read as a placeholder rather than another
 * item in the row). Composes with every variant — pairs most often with
 * `secondary pill` for a dashed pill placeholder — and changes nothing
 * but `border-style`, same modeling as `pill`.
 */
@customElement('cos-button')
export class CosButton extends LitElement {
	@property({ type: String }) variant: 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline' | 'brand-ghost' | 'link' = 'secondary';
	@property({ type: String }) size: 'sm' | 'md' | 'lg' = 'md';
	@property({ type: Boolean, reflect: true }) disabled = false;
	@property({ type: Boolean, reflect: true }) loading = false;
	@property({ type: String }) icon = '';
	@property({ type: String }) iconPosition: 'start' | 'end' = 'start';
	@property({ type: String }) type: 'button' | 'submit' | 'reset' = 'button';

	// A native submit button living inside this component's SHADOW root
	// cannot submit a form in the light DOM: form association does not cross
	// a shadow boundary. So <form><cos-button type="submit"> rendered a
	// perfectly ordinary looking button that silently did nothing, which is
	// how the Add Item form shipped with a dead "Create Record" button and
	// the Record Editor with a dead "Search". Forward the intent to the
	// containing form ourselves.
	//
	// Bound on the host: the inner button's click is composed, so it has
	// already crossed the boundary by the time it reaches us. Guarded on
	// defaultPrevented so a consumer's own @click handler calling
	// preventDefault still wins.
	private _forwardFormAction = (event: Event): void => {
		if (this.disabled || this.loading) return;
		if (this.type !== 'submit' && this.type !== 'reset') return;
		if (event.defaultPrevented) return;
		const form = this.closest('form');
		if (!form) return;
		if (this.type === 'submit') {
			form.requestSubmit();
		} else {
			form.reset();
		}
	};

	// The inner native button swallows its own clicks while disabled, but the host
	// is a box of its own (its padding, its edge, el.click()), and a click
	// there used to reach every consumer's @click and every ancestor. Stop it
	// here, in the capture phase, so no disabled or loading cos-button fires a
	// click anywhere. Registered in the constructor so it runs ahead of any
	// listener a consumer binds to the host.
	private _blockWhileDisabled = (event: Event): void => {
		if (this.disabled || this.loading) {
			event.stopImmediatePropagation();
			event.preventDefault();
		}
	};

	constructor() {
		super();
		this.addEventListener('click', this._blockWhileDisabled, true);
	}

	connectedCallback(): void {
		super.connectedCallback();
		this.addEventListener('click', this._forwardFormAction);
	}

	disconnectedCallback(): void {
		this.removeEventListener('click', this._forwardFormAction);
		super.disconnectedCallback();
	}
	@property({ type: Boolean, attribute: 'full-width', reflect: true }) fullWidth = false;
	@property({ type: String, attribute: 'aria-label' }) ariaLabel: string | null = null;
	@property({ type: Boolean, attribute: 'icon-only', reflect: true }) iconOnly = false;
	@property({ type: Boolean, reflect: true }) pill = false;
	@property({ type: Boolean, reflect: true }) dashed = false;
	/* Opt-in for a button used as a LIST ROW rather than a label: the
	   slotted content may be two lines, and either line may wrap. Every
	   size tier is a fixed height with white-space: nowrap, which suits a
	   one-line label and quietly breaks a row - the text paints over
	   whatever sits beside it and a second line spills past the row
	   divider. Sets the height free and lets the content wrap. Left off,
	   nothing about a button changes. */
	@property({ type: Boolean, reflect: true }) multiline = false;
	@property({ type: String, attribute: 'aria-expanded' }) ariaExpanded: string | null = null;
	@property({ type: String, attribute: 'aria-haspopup' }) ariaHasPopup: string | null = null;
	@property({ type: String, attribute: 'aria-controls' }) ariaControls: string | null = null;
	@property({ type: String, attribute: 'aria-current' }) ariaCurrent: string | null = null;
	@property({ type: String, attribute: 'aria-pressed' }) ariaPressed: string | null = null;
	@property({ type: String, attribute: 'button-role' }) buttonRole: string | null = null;
	/** Sets tabindex on the inner button, for a composite widget with one tab stop (roving focus). */
	@property({ type: String, attribute: 'button-tabindex' }) buttonTabindex: string | null = null;

	static styles = css`
		:host {
			display: inline-flex;
			vertical-align: middle;
		}
		:host([full-width]) { display: flex; width: 100%; }
		:host([full-width]) button { width: 100%; }

		button {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			gap: 6px;
			border-radius: var(--cos-radius-md);
			font-family: var(--cos-font-family);
			font-weight: var(--cos-font-weight-medium);
			line-height: 1;
			cursor: pointer;
			text-decoration: none;
			border: 1px solid transparent;
			transition: background-color var(--cos-transition-fast),
			            border-color var(--cos-transition-fast),
			            color var(--cos-transition-fast),
			            opacity var(--cos-transition-fast),
			            transform 160ms var(--cos-ease-out);
			white-space: nowrap;
		}

		/* Size tokens — Stage 46a tuned to spec § 2.7 (sm 28 / md 32 /
		   lg 40). Preserves the existing 32/40/48 tier as comments
		   so we know what changed. */
		button.size-sm { height: 28px; padding: 0 10px; font-size: var(--cos-font-size-sm); }
		button.size-md { height: 32px; padding: 0 14px; font-size: var(--cos-font-size-base); }
		button.size-lg { height: 40px; padding: 0 18px; font-size: var(--cos-font-size-md); }

		/* Icon-only — square to the size tier's height. The glyph is the
		   whole visible surface, so aria-label is mandatory (render warns
		   in dev when it's missing). */
		button.icon-only { padding: 0; }

		/* multiline - see the property comment. The size tier's height
		   becomes a minimum, and the padding it used to stand in for is
		   restored explicitly so a one-line row keeps the same rhythm. */
		:host([multiline]) button {
			height: auto;
			white-space: normal;
			align-items: stretch;
		}
		/* The vertical padding is picked so a ONE-line row still lands on
		   exactly its tier height (min-height wins), and only a row that
		   really wraps grows. Adding multiline therefore moves nothing
		   until the content needs the room. */
		:host([multiline]) button.size-sm { min-height: 28px; padding-top: 5px; padding-bottom: 5px; }
		:host([multiline]) button.size-md { min-height: 32px; padding-top: 6px; padding-bottom: 6px; }
		:host([multiline]) button.size-lg { min-height: 40px; padding-top: 8px; padding-bottom: 8px; }
		button.icon-only.size-sm { width: 28px; }
		button.icon-only.size-md { width: 32px; }
		button.icon-only.size-lg { width: 40px; }

		/* Pill — the chip/CTA radius override. Composes with every variant
		   and with icon-only (a round icon-only button). */
		button.pill { border-radius: var(--cos-radius-pill); }

		/* Dashed — the "add one" placeholder border. Composes with every
		   variant (most often secondary + pill, for a dashed pill
		   placeholder) and changes nothing but border-style. */
		button.dashed { border-style: dashed; }

		/* Variant tokens — Stage 46a redirects to the new design tokens.
		   primary = brand 700 (4.75:1 on white, AA-safe); hover at 800.
		   secondary = white surface + 1px brand-grey border; hover lifts
		   the border to text-secondary so it doesn't feel inert. */
		button.variant-primary {
			background: var(--cos-brand-700);
			color: var(--cos-text-on-color);
			border-color: var(--cos-brand-700);
		}
		button.variant-primary:hover:not(:disabled) {
			background: var(--cos-brand-800);
			border-color: var(--cos-brand-800);
		}

		button.variant-secondary {
			background: var(--cos-surface);
			color: var(--cos-text-primary);
			border-color: var(--cos-border);
		}
		button.variant-secondary:hover:not(:disabled) {
			background: var(--cos-surface-raised);
			border-color: var(--cos-text-secondary);
		}

		button.variant-ghost {
			background: transparent;
			color: var(--cos-text-primary);
			border-color: transparent;
		}
		button.variant-ghost:hover:not(:disabled) {
			background: var(--cos-surface-raised);
		}

		button.variant-danger {
			background: var(--cos-status-danger-solid);
			color: var(--cos-text-on-color);
			border-color: var(--cos-status-danger-solid);
		}
		button.variant-danger:hover:not(:disabled) {
			background: color-mix(in srgb, var(--cos-status-danger-solid) 85%, black);
			border-color: color-mix(in srgb, var(--cos-status-danger-solid) 85%, black);
		}

		/* danger-outline — destructive, but quiet enough to repeat down every
		   row of a table. Filled danger is for ONE prominent destructive
		   action; a column of solid red blocks reads as an error state. This
		   matches the outline treatment the hand-rolled row buttons used. */
		button.variant-danger-outline {
			background: var(--cos-surface);
			color: var(--cos-status-danger-text);
			border-color: var(--cos-status-danger-text);
		}
		button.variant-danger-outline:hover:not(:disabled) {
			background: var(--cos-status-danger-wash);
		}

		/* brand-ghost — the brand sibling of danger-outline: a quiet,
		   teal-tinted wash for a repeated brand-positive row action. Filled
		   primary is for ONE prominent action; a column of solid brand
		   blocks is as loud as a column of solid red ones. Mode-scoped
		   tokens re-theme correctly in dark automatically. */
		button.variant-brand-ghost {
			background: var(--cos-status-teal-wash);
			color: var(--cos-status-teal-text);
			border-color: transparent;
		}
		/* Hover deepens the WASH, not the ink. The ink is already the
		   mode-scoped pair to that wash, so darkening it (the obvious
		   move, and what this first shipped as) sends dark mode the
		   wrong way: the brand ramp is FIXED across modes, so reaching
		   into it puts dark teal on a dark tint — measured 2.72:1 in
		   the browser, failing even the 3:1 UI floor, while every unit
		   test stayed green. The status triplet's line step is the
		   stronger shade that moves correctly in both modes. */
		button.variant-brand-ghost:hover:not(:disabled) {
			background: var(--cos-status-teal-line);
		}

		button.variant-link {
			background: transparent;
			color: var(--cos-brand-700);
			border-color: transparent;
			padding: 0;
			height: auto;
			text-decoration: underline;
			text-underline-offset: 2px;
		}
		button.variant-link:hover:not(:disabled) {
			color: var(--cos-brand-800);
		}

		/* Disabled state — applies to all variants identically.
		   Pointer-events:none means the click never reaches the
		   inner <button>; the disabled attribute on the button
		   also blocks events, so this is belt-and-braces. */
		button:disabled {
			opacity: 0.5;
			cursor: not-allowed;
			pointer-events: none;
		}
		/* Press response: a 3% scale reads as "the click landed" before
		   the network round-trip does, including on touch where there is
		   no hover. Kept under reduced motion - it is feedback, not travel. */
		button:active:not(:disabled) {
			transform: scale(0.97);
		}
		/* An inline text link is not a pressable slab; it must not shrink. */
		button.variant-link:active {
			transform: none;
		}

		/* Focus ring — uniform across variants, Stage 46a tokens.
		   --cos-brand-700 is the AA-safe focus-ring color from
		   spec § 5 (3:1 vs white). */
		button:focus-visible {
			outline: 2px solid var(--cos-brand-700);
			outline-offset: 2px;
		}

		/* Spinner — inline CSS animation, no extra component.
		   Diameter matches the button text height so the layout
		   doesn't reflow when loading flips on. */
		.spinner {
			width: 14px;
			height: 14px;
			border-radius: 50%;
			border: 2px solid currentColor;
			border-right-color: transparent;
			animation: cos-button-spin var(--cos-spinner-duration, 700ms) linear infinite;
			flex-shrink: 0;
			opacity: 0.7;
		}
		button.size-sm .spinner { width: 12px; height: 12px; border-width: 1.5px; }
		button.size-lg .spinner { width: 16px; height: 16px; }

		@keyframes cos-button-spin {
			to { transform: rotate(360deg); }
		}

		/* Icon sizing matches the spinner so a button with an
		   icon doesn't visually jump when loading flips. */
		cos-icon { flex-shrink: 0; }
	`;

	// A bare <cos-button> host has no tabindex/delegatesFocus, so
	// `.focus()` on the host is otherwise a silent no-op, the same gap
	// cos-modal's own footer-fallback code works around by reaching into
	// this component's shadow root by hand. Forwarding here means an
	// `autofocus` attribute on a <cos-button> in a modal's body slot
	// (cos-modal's `_autofocusTarget`) actually lands focus, the same way
	// cos-input/cos-select already do.
	override focus(options?: FocusOptions): void {
		this.shadowRoot?.querySelector('button')?.focus(options);
	}

	render() {
		if (import.meta.env.DEV && this.iconOnly && !this.ariaLabel) {
			console.warn('<cos-button icon-only> without aria-label: a bare glyph reaches screen readers unnamed. Add aria-label.');
		}
		const cls = `variant-${this.variant} size-${this.size}${this.iconOnly ? ' icon-only' : ''}${this.pill ? ' pill' : ''}${this.dashed ? ' dashed' : ''}`;
		const iconSize = this.size === 'sm' ? 14 : this.size === 'lg' ? 18 : 16;
		// Loading replaces the START icon with a spinner; the end
		// icon (if any) stays visible. If there's no start icon, a
		// spinner is still rendered in the start position so the
		// label doesn't jump rightward.
		const renderIcon = (where: 'start' | 'end') => {
			if (this.iconPosition !== where) return nothing;
			if (this.loading && where === 'start') {
				return html`<span class="spinner" aria-hidden="true"></span>`;
			}
			if (!this.icon) return nothing;
			return html`<cos-icon name=${this.icon} size=${String(iconSize)}></cos-icon>`;
		};
		// Render a leading spinner when loading even if no icon is
		// configured (and no start icon position is set).
		const leadingSpinner = this.loading && !this.icon
			? html`<span class="spinner" aria-hidden="true"></span>`
			: nothing;
		return html`
			<button
				part="button"
				class=${cls}
				type=${this.type}
				role=${this.buttonRole ?? nothing}
				tabindex=${this.buttonTabindex ?? nothing}
				?disabled=${this.disabled || this.loading}
				aria-label=${this.ariaLabel ?? nothing}
				aria-disabled=${this.disabled || this.loading ? 'true' : 'false'}
				aria-busy=${this.loading ? 'true' : 'false'}
				aria-expanded=${this.ariaExpanded ?? nothing}
				aria-haspopup=${this.ariaHasPopup ?? nothing}
				aria-controls=${this.ariaControls ?? nothing}
				aria-current=${this.ariaCurrent ?? nothing}
				aria-pressed=${this.ariaPressed ?? nothing}>
				${this.iconPosition === 'start' ? renderIcon('start') : leadingSpinner}
				<slot></slot>
				${this.iconPosition === 'end' ? renderIcon('end') : nothing}
			</button>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-button': CosButton;
	}
}
